import { cloneElement, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  RiBookOpenLine,
  RiBuilding2Line,
  RiKeyboardLine,
  RiPaintBrushLine,
  RiSettings4Line,
  RiSidebarFoldLine,
  RiWaterFlashLine,
  type RemixiconComponentType,
} from '@remixicon/react';
import type { AccessibleTenant } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { NamedIcon } from './icons';
import { isPlatformOrOrgAdmin, splitTenantPath, visibleGroups, type VisibleGroup } from './nav';
import { sectionsQuery } from './sections';
import { Chevron, isUnder, rowFocus, SidebarEdge, tenantsByOrganization } from './sidebar-parts';
import { rememberTenant, useGroupOpen, useSidebarCollapsed, useSidebarGroups } from './sidebar-state';
import { SidebarRail } from './SidebarRail';
import { useMe } from './use-me';
import { UserMenu } from './UserMenu';

/**
 * "Site bar / Suv ombori daraxti" (471:7958, 01-design-system §6.2, 03-modules §1.2): logo, the "Suv omborlar" tree of
 * accessible tenants with the current tenant's module groups, global items, the account menu and "Menyuni yopish".
 * Collapsed ("Menyuni yopish"), it becomes a 64 px rail (§9 Q7 proposal).
 */
export function Sidebar({ tenant }: { tenant?: AccessibleTenant }) {
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const pathname = useLocation({ select: (l) => l.pathname });

  useEffect(() => {
    if (tenant) rememberTenant(tenant.slug);
  }, [tenant]);

  return collapsed ? (
    <SidebarRail tenant={tenant} onExpand={() => setCollapsed(false)} />
  ) : (
    <SidebarTree tenant={tenant} pathname={pathname} onCollapse={() => setCollapsed(true)} />
  );
}

function SidebarTree({ tenant, pathname, onCollapse }: { tenant?: AccessibleTenant; pathname: string; onCollapse(): void }) {
  const { t } = useTranslation('shell');
  const me = useMe();
  const [rootOpen, setRootOpen] = useState(true);
  // accordion: one tenant expanded at a time, following the route's tenant (03-modules §1.2); on user-level pages
  // the last visited tenant stays open, as in 556:16443
  const lastTenant = useSidebarGroups((s) => s.lastTenant);
  const routeTenant = tenant?.slug ?? lastTenant;
  const [expanded, setExpanded] = useState<string | null>(routeTenant);
  const [routeSlug, setRouteSlug] = useState(routeTenant);
  if (routeSlug !== routeTenant) {
    setRouteSlug(routeTenant);
    setExpanded(routeTenant);
  }

  // keep the current page's row in view when the tree is taller than the sidebar (scrolls the tree box only)
  const treeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = treeRef.current;
    const row = box?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!box || !row) return;
    const b = box.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    if (r.top < b.top) box.scrollTop -= b.top - r.top;
    else if (r.bottom > b.bottom) box.scrollTop += r.bottom - b.bottom;
  }, [pathname]);

  const orgs = tenantsByOrganization(me);
  const tenantNode = (x: AccessibleTenant) => (
    <TenantNode
      key={x.id}
      tenant={x}
      expanded={expanded === x.slug}
      onToggle={() => setExpanded((cur) => (cur === x.slug ? null : x.slug))}
      pathname={pathname}
    />
  );

  return (
    <aside className="relative flex h-full w-240 shrink-0 flex-col gap-20 bg-sidebar px-8 py-20">
      <SidebarEdge />
      <div className="flex h-45 shrink-0 items-center justify-center px-8 py-6">
        <img src="/logo-uge.webp" alt="O'zbekgidroenergo" className="h-33 w-60 object-contain" />
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-between gap-8">
        <nav aria-label={t('nav.label')} className="flex min-h-0 flex-col gap-6">
          <button
            type="button"
            aria-expanded={rootOpen}
            onClick={() => setRootOpen((v) => !v)}
            className={cn('flex h-36 w-full shrink-0 items-center rounded-4 bg-accent px-8 text-left', rowFocus)}
          >
            <span className="flex min-w-0 flex-1 items-center gap-8">
              <RiWaterFlashLine aria-hidden className="size-20 shrink-0 text-icon" />
              <span className="truncate text-sm text-fg-nav">{t('nav.reservoirs')}</span>
            </span>
            <span className="flex w-40 shrink-0 justify-end">
              <Chevron open={rootOpen} />
            </span>
          </button>

          {/* only the tenant tree scrolls; the global rows below stay visible. The scroll box reaches into the
              sidebar's right padding so its scrollbar never narrows the 224-wide rows. */}
          {rootOpen && (
            <div ref={treeRef} className="-mr-8 min-h-0 overflow-y-auto">
              <div className="flex w-224 gap-6 pl-6">
                <span aria-hidden className="w-px shrink-0 bg-line" />
                <div className="flex min-w-0 flex-1 flex-col gap-6">
                  {me.tenants.length === 0 && <p className="px-8 py-6 t-field text-fg-tertiary">{t('nav.noTenants')}</p>}
                  {orgs.length > 1
                    ? orgs.map((o) => (
                        <div key={o.id} className="flex flex-col gap-6">
                          <p className="truncate px-8 pt-8 t-caps">{o.name}</p>
                          {o.tenants.map(tenantNode)}
                        </div>
                      ))
                    : me.tenants.map(tenantNode)}
                </div>
              </div>
            </div>
          )}

          {isPlatformOrOrgAdmin(me) && (
            <GlobalLink to="/admin" icon={RiBuilding2Line} label={t('nav.organizations')} active={/^\/admin(\/|$)/.test(pathname)} />
          )}
          <SettingsGroup pathname={pathname} />
          <GlobalLink to="/guide" icon={RiBookOpenLine} label={t('nav.guide')} active={/^\/guide(\/|$)/.test(pathname)} />
        </nav>

        <UserMenu tenant={tenant} />
      </div>

      <button
        type="button"
        onClick={onCollapse}
        className={cn('flex h-32 w-full shrink-0 items-start gap-8 rounded-4 px-8 pt-4 text-left hover:bg-hover', rowFocus)}
      >
        <RiSidebarFoldLine aria-hidden className="size-20 shrink-0 text-icon" />
        <span className="truncate text-sm text-fg-secondary">{t('nav.collapse')}</span>
      </button>
    </aside>
  );
}

/** L1 — a tenant ("Chotqol suv ombori"): name → its "Bosh panel", chevron → expand its modules. */
function TenantNode({
  tenant,
  expanded,
  onToggle,
  pathname,
}: {
  tenant: AccessibleTenant;
  expanded: boolean;
  onToggle(): void;
  pathname: string;
}) {
  const { t } = useTranslation('shell');
  const groups = visibleGroups(tenant.permissions);
  const canExpand = groups.length > 0;
  const open = expanded && canExpand;
  return (
    <>
      <div className="flex h-32 w-full shrink-0 rounded-4 hover:bg-hover">
        {/* Figma "Menu" quirk kept: the 36-high left add-on puts the label 8 px below the row top */}
        <Link
          to="/t/$tenantSlug"
          params={{ tenantSlug: tenant.slug }}
          className={cn('flex min-w-0 flex-1 items-start rounded-4 pt-8 pl-8', !canExpand && 'pr-8', rowFocus)}
        >
          <span className={cn('truncate text-sm text-fg-nav', open ? 'font-semibold' : 'font-normal')}>{tenant.name}</span>
        </Link>
        {canExpand && (
          <button
            type="button"
            aria-expanded={open}
            aria-label={t(open ? 'nav.collapseItem' : 'nav.expandItem', { name: tenant.name })}
            onClick={onToggle}
            className={cn('flex w-48 shrink-0 items-center justify-end rounded-4 pr-8', rowFocus)}
          >
            <Chevron open={open} />
          </button>
        )}
      </div>
      {open && (
        <div className="flex shrink-0 gap-4">
          <span aria-hidden className="w-px shrink-0 bg-line" />
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {groups.map((g) => (
              <GroupNode key={g.id} group={g} tenant={tenant} pathname={pathname} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/** L2 — module group with a coloured chip ("Guruh"); its pages are L3 rows. */
function GroupNode({ group, tenant, pathname }: { group: VisibleGroup; tenant: AccessibleTenant; pathname: string }) {
  const { t } = useTranslation('shell');
  const route = splitTenantPath(pathname);
  const here = route?.slug === tenant.slug ? route.rest : null;
  const inside = group.leaves.some((l) => isUnder(here, l.segment)) || (group.sections && isUnder(here, 'sections'));
  const [open, toggle] = useGroupOpen(group.id, inside);

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className={cn('flex h-32 w-full items-center rounded-4 pr-4 pl-6 text-left hover:bg-hover', rowFocus)}
      >
        <span className="flex min-w-0 flex-1 items-center gap-6">
          <span aria-hidden className={cn('flex size-24 shrink-0 items-center justify-center rounded-6 text-white', group.chipClass)}>
            <group.icon className="size-16" />
          </span>
          <span className="truncate text-13 leading-20 font-medium tracking-figma text-fg-nav">{t(group.labelKey)}</span>
        </span>
        <span className="flex w-40 shrink-0 justify-end">
          <Chevron open={open} />
        </span>
      </button>
      {open && (
        <Branch>
          {group.leaves.map((leaf) => (
            <Leaf
              key={leaf.id}
              active={isUnder(here, leaf.segment)}
              icon={<leaf.icon aria-hidden className={cn('size-16 shrink-0', leaf.iconClass)} />}
              label={t(leaf.labelKey)}
              link={<Link to={leaf.to} params={{ tenantSlug: tenant.slug }} />}
            />
          ))}
          {group.sections && <SectionLeaves tenant={tenant} here={here} />}
        </Branch>
      )}
    </div>
  );
}

/** Constructor group: one leaf per section → its editor (icon and colour of the section). */
function SectionLeaves({ tenant, here }: { tenant: AccessibleTenant; here: string | null }) {
  const { data: sections } = useQuery(sectionsQuery(tenant.id));
  return (sections ?? []).map((s) => (
    <Leaf
      key={s.id}
      active={isUnder(here, `sections/${s.id}`)}
      icon={
        s.icon.kind === 'preset' ? (
          <span className="flex size-16 shrink-0" style={{ color: s.color }}>
            <NamedIcon name={s.icon.name} className="size-16" />
          </span>
        ) : (
          <span aria-hidden className="size-16 shrink-0 rounded-4" style={{ backgroundColor: s.color }} />
        )
      }
      label={s.name}
      dimmed={s.hidden}
      link={<Link to="/t/$tenantSlug/sections/$sectionId/edit" params={{ tenantSlug: tenant.slug, sectionId: s.id }} />}
    />
  ));
}

/** Children container with the 1 px guide rail ("Ichki daraja": padding-left 14, gap 1). */
function Branch({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 gap-1 pl-14">
      <span aria-hidden className="w-px shrink-0 bg-line" />
      <div className="flex min-w-0 flex-1 flex-col gap-4">{children}</div>
    </div>
  );
}

type LinkElement = ReactElement<{ className?: string; children?: ReactNode; 'aria-current'?: 'page' }>;

/** L3 row ("Band"): h32, padding 0/2, icon 16 + label 14/20 (gap 5); selected = White/10. */
function Leaf({ active, icon, label, link, dimmed }: { active: boolean; icon: ReactNode; label: string; link: LinkElement; dimmed?: boolean }) {
  return cloneElement(
    link,
    {
      className: cn('flex h-32 w-full items-center gap-5 rounded-4 px-2', active ? 'bg-selected' : 'hover:bg-hover', dimmed && 'opacity-50', rowFocus),
      'aria-current': active ? 'page' : undefined,
    },
    icon,
    <span className="truncate text-sm text-fg-nav">{label}</span>,
  );
}

/**
 * "Sozlamalar" with its two pages (556:16443): the header row stays dimmed like the design, children are not.
 * Open on settings pages (a toggle there lasts for the visit), elsewhere it follows the user's saved choice.
 */
function SettingsGroup({ pathname }: { pathname: string }) {
  const { t } = useTranslation('shell');
  const [open, toggle] = useGroupOpen('settings', /^\/settings(\/|$)/.test(pathname));
  const items = [
    { to: '/settings/interface' as const, icon: RiPaintBrushLine, label: t('nav.interfaceSettings') },
    { to: '/settings/hotkeys' as const, icon: RiKeyboardLine, label: t('nav.hotkeys') },
  ];
  return (
    <>
      <div className="flex h-32 w-full shrink-0 rounded-4 opacity-50 hover:bg-hover hover:opacity-100">
        <Link to="/settings" className={cn('flex min-w-0 flex-1 items-start gap-8 rounded-4 pt-8 pl-8', rowFocus)}>
          <RiSettings4Line aria-hidden className="size-20 shrink-0 text-icon" />
          <span className="truncate text-sm text-fg-nav">{t('nav.settings')}</span>
        </Link>
        <button
          type="button"
          aria-expanded={open}
          aria-label={t(open ? 'nav.collapseItem' : 'nav.expandItem', { name: t('nav.settings') })}
          onClick={toggle}
          className={cn('flex w-48 shrink-0 items-center justify-end rounded-4 pr-8', rowFocus)}
        >
          <Chevron open={open} />
        </button>
      </div>
      {open && (
        <Branch>
          {items.map((it) => (
            <Leaf
              key={it.to}
              active={pathname === it.to || pathname.startsWith(it.to + '/')}
              icon={<it.icon aria-hidden className="size-16 shrink-0 text-fg-nav" />}
              label={it.label}
              link={<Link to={it.to} />}
            />
          ))}
        </Branch>
      )}
    </>
  );
}

/** Global rows ("Foydalanish qo’llanmasi", "Tashkilotlar"): icon 20 + label, at 50 % like the design. */
function GlobalLink({ to, icon: Icon, label, active }: { to: '/guide' | '/admin'; icon: RemixiconComponentType; label: string; active: boolean }) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex h-32 w-full shrink-0 items-start gap-8 rounded-4 px-8 pt-8',
        active ? 'bg-selected' : 'opacity-50 hover:bg-hover hover:opacity-100',
        rowFocus,
      )}
    >
      <Icon aria-hidden className="size-20 shrink-0 text-icon" />
      <span className="truncate text-sm text-fg-nav">{label}</span>
    </Link>
  );
}
